import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Archive, Phone, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { friendlyError, reportError } from "../lib/errorReporter";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import OwnerBadge from "../components/OwnerBadge";
import ConfirmDialog from "../components/ConfirmDialog";
import EmailSendDialog from "../components/quotation/EmailSendDialog";
import EmailHistory from "../components/quotation/EmailHistory";
import RenewDialog from "../components/quotation/RenewDialog";
import Timeline from "../components/quotation/Timeline";
import DeliveryAlert from "../components/quotation/DeliveryAlert";
import ReminderCard from "../components/quotation/ReminderCard";
import { fmtBytes, fmtDate, fmtDateTime, fmtMoney } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { ownerLabel } from "../lib/ownerDisplay";
import { managerMayReassign } from "../lib/quotationVisibility";

function Section({ title, actions, children }) {
  return (
    <div className="qw-card" style={{ marginBottom: 16 }}>
      <div className="hstack" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <h2 style={{ fontWeight: 600, fontSize: 14, margin: 0 }} className="qw-break">{title}</h2>
        <div className="hstack">{actions}</div>
      </div>
      {children}
    </div>
  );
}

function KeyValue({ rows }) {
  return (
    <dl className="qw-kv">
      {rows.map(([label, value]) => (
        <div className="qw-kv-row" key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}


function ChangeOwnerDialog({
  open, currentOwner, members, onCancel, onConfirm, submitting, error, t,
  allowUnassigned = true, excludeUserId = null, title, newLabel,
}) {
  const options = (members || []).filter(
    (m) => !excludeUserId || m.user_id !== excludeUserId,
  );
  const initial = () => {
    if (allowUnassigned) return currentOwner?.user_id || "unassigned";
    return options[0]?.user_id || "";
  };
  const [selected, setSelected] = useState(initial);
  useEffect(() => {
    if (open) setSelected(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentOwner, excludeUserId, members]);
  if (!open) return null;
  const submit = () => {
    onConfirm(selected === "unassigned" ? null : selected);
  };
  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-owner-title"
        data-testid="change-owner-dialog"
      >
        <div className="h" id="change-owner-title">{title || t("quotations.change_owner_title")}</div>
        <div className="body">
          <div style={{ marginBottom: 8, color: "#64748b", fontSize: 13 }}>
            {t("quotations.change_owner_current")}:
          </div>
          <div style={{ marginBottom: 12 }}>
            <OwnerBadge owner={currentOwner || null} />
          </div>
          <label htmlFor="change-owner-select" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            {newLabel || t("quotations.change_owner_new")}
          </label>
          <select
            id="change-owner-select"
            className="qw-owner-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="change-owner-select"
          >
            {allowUnassigned && (
              <option value="unassigned">{t("quotations.owner_unassigned")}</option>
            )}
            {options.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {ownerLabel(m, t)} · {m.role === "owner" ? t("settings.role_owner") : t("settings.role_sales")}
              </option>
            ))}
          </select>
          {error && <div className="qw-error" style={{ marginTop: 8 }}>{error}</div>}
        </div>
        <div className="footer">
          <button type="button" className="qw-btn ghost" onClick={onCancel} data-testid="change-owner-cancel">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="qw-btn primary"
            onClick={submit}
            disabled={submitting || !selected}
            data-testid="change-owner-confirm"
          >
            {submitting ? t("common.loading") : t("quotations.change_owner_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuotationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { currentOrg, user } = useAuth();
  const isOwnerRole = currentOrg?.role === "owner";
  const isManagerRole = currentOrg?.role === "manager";
  const currentUserId = user?.id || null;
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  // Phase 2D.3B.2 — the Manager's own, server-scoped candidate list: the active
  // Sales of the team it currently leads, without itself.
  const [teamSales, setTeamSales] = useState([]);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerChangeSubmitting, setOwnerChangeSubmitting] = useState(false);
  const [ownerChangeError, setOwnerChangeError] = useState("");
  // Phase 2C.2 — archive
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const archiveInFlight = useRef(false);
  // Public-link revoke shares the same reusable confirmation dialog.
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  // Phase 3B — renewal of an expired quotation.
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [renewError, setRenewError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [revokeError, setRevokeError] = useState("");
  const revokeInFlight = useRef(false);
  // Phase 2B.1 — email
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogSource, setEmailDialogSource] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState("");
  const [emailConfig, setEmailConfig] = useState(null);
  const fileRef = useRef(null);
  const emailSectionRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await api.get(`/quotations/${id}`);
      setData(r.data);
      try {
        const p = await api.get(`/quotations/${id}/preview`);
        setPreview(p.data);
      } catch (previewErr) {
        reportError("quotationDetail.preview", previewErr);
        setPreview(null);
      }
    } catch (err) {
      reportError("quotationDetail.load", err);
      // A quotation that does not exist and one this user may not open answer
      // the same generic 404 on purpose — so they get the same Bulgarian copy.
      if (err?.response?.status === 404) {
        setError(t("quotations.not_accessible"));
        return;
      }
      setError(friendlyError(err, t("common.error_generic")));
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isOwnerRole) return;
    api
      .get("/organization/members/assignable")
      .then((r) => setMembers(r.data || []))
      .catch((err) => reportError("quotationDetail.loadMembers", err));
  }, [isOwnerRole]);

  useEffect(() => {
    if (!isManagerRole) return;
    api
      .get("/organization/members/reassignable")
      .then((r) => setTeamSales(r.data || []))
      .catch((err) => reportError("quotationDetail.loadTeamSales", err));
  }, [isManagerRole]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  // Phase 3B — the customer list is only needed by the renewal form, so it is
  // fetched when the dialog is opened for the first time.
  useEffect(() => {
    if (!renewDialogOpen || customers.length) return;
    api
      .get("/customers")
      .then((r) => setCustomers(r.data || []))
      .catch((err) => reportError("quotationDetail.loadCustomers", err));
  }, [renewDialogOpen, customers.length]);

  const loadEmails = useCallback(async () => {
    setEmailsError("");
    setEmailsLoading(true);
    try {
      const r = await api.get(`/quotations/${id}/emails`);
      setEmails(r.data || []);
    } catch (err) {
      reportError("quotationDetail.loadEmails", err);
      setEmailsError(friendlyError(err, t("common.error_generic")));
    } finally {
      setEmailsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadEmails();
    api
      .get("/email/config-status")
      .then((r) => setEmailConfig(r.data))
      .catch((err) => reportError("quotationDetail.loadEmailConfig", err));
  }, [loadEmails]);

  const focusEmailSection = useCallback(() => {
    emailSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // A resend keeps pointing at the attempt it repeats, so the existing
  // .../emails/{id}/resend endpoint is used whenever there is an attempt to
  // repeat. A quotation marked sent by hand has none, and then the plain send
  // path IS the resend — the server allows it for sent / opened / question.
  const latestResendableEmail = useMemo(
    () =>
      (emails || []).find(
        (e) => ["sent", "delivered", "opened", "question"].indexOf(e.status) >= 0,
      ) || null,
    [emails],
  );

  const copyLink = useCallback(async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      reportError("quotationDetail.copy", err);
    }
  }, []);

  // Phase 3B — renewal. A NEW draft quotation is created from the expired one;
  // the expired quotation itself is never touched.
  const doRenew = useCallback(
    async (payload, file) => {
      if (renewSubmitting) return;
      setRenewSubmitting(true);
      setRenewError("");
      let created = null;
      try {
        const r = await api.post(`/quotations/${id}/renew`, payload);
        created = r.data?.quotation || null;
        if (r.data?.file && r.data.file.copied === false) {
          setToast(t("quotations.renew_file_copy_failed"));
        }
      } catch (err) {
        reportError("quotationDetail.renew", err);
        setRenewError(friendlyError(err, t("common.error_generic")));
        setRenewSubmitting(false);
        if (err?.response?.data?.detail?.code === "already_renewed") load();
        return;
      }
      // The draft EXISTS from here on. An upload failure is reported and the user
      // continues on the new quotation - it is never rolled back.
      if (file && created?.id) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/quotations/${created.id}/file`, fd);
        } catch (err) {
          reportError("quotationDetail.renewUpload", err);
          setToast(t("quotations.renew_file_upload_failed"));
        }
      }
      setRenewSubmitting(false);
      setRenewDialogOpen(false);
      if (created?.id) nav(`/quotations/${created.id}`);
    },
    [id, load, nav, renewSubmitting, t],
  );

  const doSend = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${id}/send`);
      await copyLink(r.data.public_url);
      load();
    } catch (err) {
      reportError("quotationDetail.send", err);
      setError(friendlyError(err, t("common.error_generic")));
    } finally {
      setBusy(false);
    }
  }, [id, copyLink, load, t]);

  const recover = useCallback(async () => {
    try {
      const r = await api.post(`/quotations/${id}/link/recover`);
      await copyLink(r.data.public_url);
    } catch (err) {
      reportError("quotationDetail.recover", err);
      setError(friendlyError(err, t("common.error_generic")));
    }
  }, [id, copyLink, t]);

  const revoke = useCallback(async () => {
    if (revokeInFlight.current || revokeSubmitting) return;
    revokeInFlight.current = true;
    setRevokeSubmitting(true);
    setRevokeError("");
    try {
      await api.post(`/quotations/${id}/link/revoke`);
      setRevokeDialogOpen(false);
      setToast(t("quotations.revoke_link"));
      load();
    } catch (err) {
      reportError("quotationDetail.revoke", err);
      setRevokeError(t("quotations.revoke_error"));
    } finally {
      revokeInFlight.current = false;
      setRevokeSubmitting(false);
    }
  }, [id, load, t, revokeSubmitting]);

  const archive = useCallback(async () => {
    // Ref, not state: setArchiveSubmitting only takes effect on the next render,
    // so state alone cannot stop clicks fired in the same event-loop tick.
    if (archiveInFlight.current || archiveSubmitting) return;
    archiveInFlight.current = true;
    setArchiveSubmitting(true);
    setArchiveError("");
    try {
      const r = await api.post(`/quotations/${id}/archive`);
      setArchiveDialogOpen(false);
      setToast(
        r?.data?.already_archived
          ? t("quotations.archive_already")
          : t("quotations.archive_success"),
      );
      load();
    } catch (err) {
      reportError("quotationDetail.archive", err);
      const detail = err?.response?.data?.detail;
      if (detail === "not_quotation_owner") {
        setArchiveError(t("quotations.archive_forbidden"));
      } else if (detail === "archive_conflict" || detail === "invalid_status_transition") {
        setArchiveError(t("quotations.archive_conflict"));
      } else {
        setArchiveError(t("quotations.archive_error"));
      }
    } finally {
      archiveInFlight.current = false;
      setArchiveSubmitting(false);
    }
  }, [id, load, t, archiveSubmitting]);

  const uploadFile = useCallback(async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/quotations/${id}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      load();
    } catch (err) {
      reportError("quotationDetail.upload", err);
      setError(friendlyError(err, t("common.error_generic")));
    }
  }, [id, load, t]);

  const downloadFile = useCallback(
    async (fid) => {
      try {
        const r = await api.get(`/quotations/${id}/file/${fid}/signed-url`);
        window.open(r.data.url, "_blank", "noopener,noreferrer");
      } catch (err) {
        reportError("quotationDetail.download", err);
        setError(friendlyError(err, t("common.error_generic")));
      }
    },
    [id, t],
  );

  const submitOwnerChange = useCallback(
    async (newOwnerId) => {
      setOwnerChangeSubmitting(true);
      setOwnerChangeError("");
      try {
        await api.patch(`/quotations/${id}/owner`, {
          owner_user_id: newOwnerId,
          // Compare-and-set: the owner this dialog was opened on. The Manager
          // contract refuses the write if somebody else moved it meanwhile.
          expected_owner_user_id: data?.quotation?.owner_user_id || null,
        });
        setOwnerDialogOpen(false);
        load();
      } catch (err) {
        reportError("quotationDetail.changeOwner", err);
        const detail = err?.response?.data?.detail;
        if (detail === "owner_only") {
          setOwnerChangeError(t("quotations.change_owner_forbidden"));
        } else if (detail === "quotation_not_open") {
          setOwnerChangeError(t("quotations.reassign_not_open"));
        } else if (detail === "owner_changed_meanwhile") {
          setOwnerChangeError(t("quotations.reassign_stale"));
        } else if (
          [
            "manager_only",
            "manager_without_active_team",
            "current_owner_not_sales",
            "current_owner_outside_team",
            "new_owner_not_sales",
            "new_owner_outside_team",
          ].includes(detail)
        ) {
          setOwnerChangeError(t("quotations.reassign_forbidden"));
        } else if (detail === "new_owner_inactive" || detail === "new_owner_not_member") {
          setOwnerChangeError(t("quotations.change_owner_error"));
        } else {
          setOwnerChangeError(friendlyError(err, t("quotations.change_owner_error")));
        }
      } finally {
        setOwnerChangeSubmitting(false);
      }
    },
    [id, load, t, data],
  );

  const sortedEvents = useMemo(() => {
    if (!data?.timeline) return [];
    // Backend orders by created_at desc and pre-normalizes every entry.
    return data.timeline;
  }, [data]);

  if (!data) {
    return (
      <Layout title={t("quotations.detail")}>
        <div
          className="qw-empty"
          data-testid={error ? "quotation-detail-error" : "quotation-detail-loading"}
        >
          {error || t("common.loading")}
        </div>
      </Layout>
    );
  }
  const q = data.quotation;
  const isArchived = q.status === "archived";
  const canSend = q.status === "draft";
  // Server-side is authoritative (quowada.quotation_status.may_archive); this
  // only avoids offering an action that would be refused.
  const ownsQuotation = Boolean(q.owner_user_id) && q.owner_user_id === currentUserId;
  const canArchive = !isArchived && (isOwnerRole || ownsQuotation);
  const canEmailFirst = q.status === "draft";
  const canEmailResend = ["sent", "opened", "question"].includes(q.status);
  const activeLink = data.active_link;
  const owner = q.owner || null;
  // Phase 2D.3B.2 — the Manager reassignment control. The gate lives in the
  // shared mirror module; the candidate list is the server-scoped one.
  const canReassignInTeam = managerMayReassign({
    role: currentOrg?.role,
    status: q.status,
    ownerUserId: q.owner_user_id || null,
    candidateUserIds: teamSales.map((m) => m.user_id),
  });
  const sender = data.sender || null;
  // Phase 3B — the renewal lineage. Only an expired quotation without a
  // successor may be renewed; the server enforces both, and the database
  // guarantees the single successor.
  const renewal = data.renewal || null;
  const renewedFrom = data.renewed_from || null;
  const canRenew = q.status === "expired" && !renewal;
  const hasFile = (data.files || []).length > 0;

  return (
    <Layout title={t("quotations.detail")}>
      <div className="qw-detail-head">
        <div className="qw-detail-titles">
          <h1 className="h1">{q.title}</h1>
          <div className="lead">{q.reference_number} · {fmtMoney(q.value, q.currency)}</div>
        </div>
        <div className="qw-detail-actions">
          <StatusBadge status={q.status} />
          {canSend && (
            <button className="qw-btn primary" disabled={busy} onClick={doSend} data-testid="btn-mark-sent">
              {t("quotations.mark_sent")}
            </button>
          )}
          {(canEmailFirst || canEmailResend) && (
            <button
              className="qw-btn primary"
              onClick={() => {
                setEmailDialogSource(canEmailFirst ? null : latestResendableEmail);
                setEmailDialogOpen(true);
              }}
              data-testid={canEmailFirst ? "btn-email-send" : "btn-email-resend"}
            >
              {canEmailFirst ? t("quotations.email_send") : t("quotations.email_resend")}
            </button>
          )}
          {activeLink && (
            <button className="qw-btn" onClick={recover} data-testid="btn-recover-link">
              {t("quotations.copy_link")}
            </button>
          )}
          {activeLink && (
            <button className="qw-btn danger" onClick={() => setRevokeDialogOpen(true)} data-testid="btn-revoke-link">
              {t("quotations.revoke_link")}
            </button>
          )}
          {canArchive && (
            <button
              className="qw-btn ghost"
              onClick={() => { setArchiveError(""); setArchiveDialogOpen(true); }}
              data-testid="btn-archive"
            >
              {t("quotations.archive")}
            </button>
          )}
          {canRenew && (
            <button
              className="qw-btn primary"
              onClick={() => { setRenewError(""); setRenewDialogOpen(true); }}
              data-testid="btn-renew"
            >
              {t("quotations.renew")}
            </button>
          )}
        </div>
      </div>
      {error && <div className="qw-error" style={{ marginTop: 12 }}>{error}</div>}
      {isArchived && (
        <div className="qw-archived-banner" data-testid="quotation-archived-banner">
          <Archive size={18} strokeWidth={2} className="icon" aria-hidden="true" />
          <div className="text">
            <div className="t">{t("quotations.archived_banner_title")}</div>
            <div className="m">{t("quotations.archived_banner_body")}</div>
            {q.archived_at && (
              <div className="when" data-testid="quotation-archived-at">
                {t("quotations.archived_at_label")}: {fmtDateTime(q.archived_at)}
              </div>
            )}
          </div>
        </div>
      )}
      <DeliveryAlert alert={data.email_alert} onFocusEmails={focusEmailSection} />
      {(renewal || renewedFrom) && (
        <div className="qw-archived-banner" data-testid="quotation-renewal-banner">
          <RefreshCw size={18} strokeWidth={2} className="icon" aria-hidden="true" />
          <div className="text">
            <div className="t">
              {renewal
                ? t("quotations.renewed_as", { reference: renewal.reference_number })
                : t("quotations.renewed_from", { reference: renewedFrom.reference_number })}
            </div>
            <div className="m">
              <Link
                to={`/quotations/${(renewal || renewedFrom).id}`}
                data-testid={renewal ? "renewal-successor-link" : "renewal-source-link"}
                style={{ color: "#0f766e", fontWeight: 600 }}
              >
                {renewal ? t("quotations.renewal_open") : t("quotations.renewal_open_source")}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Ownership + sender header */}
      <div className="qw-card" style={{ marginTop: 16, marginBottom: 0 }} data-testid="quotation-owner-section">
        <div className="qw-owner-section">
          <div className="qw-owner-row">
            <div className="qw-owner-label">{t("quotations.owner_field_label")}</div>
            <div className="qw-owner-value" data-testid="quotation-owner-value">
              <OwnerBadge owner={owner} testid="quotation-owner-badge" />
            </div>
            {isOwnerRole && (
              <div className="qw-owner-action">
                <button
                  type="button"
                  className="qw-btn sm"
                  onClick={() => setOwnerDialogOpen(true)}
                  data-testid="btn-change-owner"
                >
                  {t("quotations.change_owner")}
                </button>
              </div>
            )}
            {canReassignInTeam && (
              <div className="qw-owner-action">
                <button
                  type="button"
                  className="qw-btn sm"
                  onClick={() => setOwnerDialogOpen(true)}
                  data-testid="btn-manager-reassign"
                >
                  {t("quotations.reassign_team_member")}
                </button>
              </div>
            )}
          </div>
          <div className="qw-owner-row">
            <div className="qw-owner-label">{t("quotations.sender_label")}</div>
            <div className="qw-owner-value" data-testid="quotation-sender-value">
              {sender ? <OwnerBadge owner={sender} small testid="quotation-sender-badge" /> : <span style={{ color: "#94a3b8" }}>—</span>}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 20 }} />
      <div className="qw-grid cols-2">
        <div>
          <Section title={t("quotations.customer_info")}>
            <KeyValue
              rows={[
                [t("customers.fields.name"), q.customers?.name || "—"],
                [t("customers.fields.email"), q.customer_email || q.customers?.email || "—"],
                [t("customers.fields.phone"), q.customer_phone || q.customers?.phone || "—"],
              ]}
            />
          </Section>
          <Section
            title={t("quotations.original_file")}
            actions={
              !isArchived && (
                <>
                  <input
                    type="file"
                    accept=".pdf,.docx,.xlsx"
                    ref={fileRef}
                    style={{ display: "none" }}
                    onChange={uploadFile}
                  />
                  <button className="qw-btn sm" onClick={() => fileRef.current?.click()} data-testid="btn-upload-more">
                    + {t("quotations.fields.upload")}
                  </button>
                </>
              )
            }
          >
            {data.files?.length === 0 ? (
              <div className="qw-empty">{t("quotations.no_file")}</div>
            ) : (
              <div className="qw-file-list">
                {data.files.map((f) => (
                  <div key={f.id} className="qw-file-row">
                    <div className="qw-file-info">
                      <div className="qw-file-name" title={f.original_name}>{f.original_name}</div>
                      <div className="qw-file-meta">
                        {f.file_ext.toUpperCase()} · {fmtBytes(f.file_size)}
                      </div>
                    </div>
                    <button className="qw-btn sm" onClick={() => downloadFile(f.id)} data-testid={`btn-download-${f.id}`}>
                      {t("quotations.download_file")}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {preview && preview.type === "pdf" && (
              <div className="qw-file-preview">
                <iframe src={preview.url} title="pdf" />
              </div>
            )}
          </Section>
          <Section title={t("quotations.public_link")}>
            {!activeLink && <div className="qw-empty">{t("quotations.no_active_link")}</div>}
            {activeLink && (
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }} className="qw-break">
                  Префикс: <code>{activeLink.token_prefix}…</code>
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  {t("quotations.valid_until")}: {fmtDateTime(activeLink.expires_at) || "—"}
                </div>
                <div className="hstack" style={{ marginTop: 8 }}>
                  <button className="qw-btn sm" onClick={recover} data-testid="btn-copy-link">
                    {copied ? t("common.copied") : t("common.copy")}
                  </button>
                </div>
              </div>
            )}
          </Section>
        </div>
        <div>
          {/* Phase 3C.3 — automatic reminders. The card fetches and saves its
              own state through /api/quotations/{id}/reminders, which authorizes
              through the SAME quotation access contract this page already
              passed; no role rule is decided here. */}
          <ReminderCard quotationId={id} quotationStatus={q.status} />
          <Section title={t("quotations.timeline")}>
            <Timeline items={sortedEvents} />
          </Section>
          {data.responses?.length > 0 && (
            <Section title="Отговор от клиент">
              {data.responses.map((r) => (
                <div key={r.id} style={{ padding: 12, background: "#f8fafc", borderRadius: 8, marginBottom: 8, minWidth: 0 }}>
                  <div className="hstack" style={{ justifyContent: "space-between" }}>
                    <StatusBadge status={r.response_type === "question" ? "question" : r.response_type} />
                    <div style={{ fontSize: 12, color: "#64748b" }}>{fmtDateTime(r.created_at)}</div>
                  </div>
                  {r.customer_name && <div className="qw-break" style={{ marginTop: 6, fontWeight: 600 }}>{r.customer_name}</div>}
                  {r.message && <div className="qw-break" style={{ marginTop: 6, fontSize: 13 }}>{r.message}</div>}
                  {r.callback_requested && r.response_type === "question" && (
                    <div className="qw-callback-badge" data-testid={`response-callback-${r.id}`}>
                      <Phone size={13} strokeWidth={2} aria-hidden="true" />
                      <span>{t("quotations.callback_requested_badge")}</span>
                    </div>
                  )}
                  {r.decline_reason && (
                    <div className="qw-break" style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>Причина: {r.decline_reason}</div>
                  )}
                </div>
              ))}
            </Section>
          )}
          {q.internal_notes && (
            <Section title="Вътрешни бележки">
              <div className="qw-break" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{q.internal_notes}</div>
            </Section>
          )}
          <div ref={emailSectionRef} data-testid="email-history-section">
            <Section title={t("quotations.email_history_title")}>
              <EmailHistory
                items={emails}
                loading={emailsLoading}
                error={emailsError}
                onResend={
                  canEmailResend
                    ? (row) => {
                        setEmailDialogSource(row);
                        setEmailDialogOpen(true);
                      }
                    : undefined
                }
              />
            </Section>
          </div>
          <span style={{ display: "none" }} data-testid="fmt-date-guard">{fmtDate(q.created_at)}</span>
        </div>
      </div>
      {copied && <div className="qw-toast">{t("common.copied")}</div>}
      {!copied && toast && (
        <div className="qw-toast" data-testid="quotation-toast" role="status">{toast}</div>
      )}
      <ConfirmDialog
        open={archiveDialogOpen}
        title={t("quotations.archive_confirm_title")}
        body={t("quotations.archive_confirm_body")}
        confirmLabel={t("quotations.archive_confirm_action")}
        cancelLabel={t("common.cancel")}
        submittingLabel={t("quotations.archive_submitting")}
        onConfirm={archive}
        onCancel={() => setArchiveDialogOpen(false)}
        submitting={archiveSubmitting}
        error={archiveError}
        testId="archive-confirm-dialog"
      />
      <ConfirmDialog
        open={revokeDialogOpen}
        title={t("quotations.revoke_confirm_title")}
        body={t("quotations.revoke_confirm_body")}
        confirmLabel={t("quotations.revoke_confirm_action")}
        cancelLabel={t("common.cancel")}
        submittingLabel={t("quotations.revoke_submitting")}
        onConfirm={revoke}
        onCancel={() => setRevokeDialogOpen(false)}
        submitting={revokeSubmitting}
        error={revokeError}
        tone="danger"
        testId="revoke-confirm-dialog"
      />
      <ChangeOwnerDialog
        open={ownerDialogOpen}
        currentOwner={owner}
        members={isOwnerRole ? members : teamSales}
        allowUnassigned={isOwnerRole}
        excludeUserId={isOwnerRole ? null : q.owner_user_id || null}
        title={isOwnerRole ? undefined : t("quotations.reassign_title")}
        newLabel={isOwnerRole ? undefined : t("quotations.reassign_new")}
        onCancel={() => setOwnerDialogOpen(false)}
        onConfirm={submitOwnerChange}
        submitting={ownerChangeSubmitting}
        error={ownerChangeError}
        t={t}
      />
      <EmailSendDialog
        open={emailDialogOpen}
        quotation={{ ...q, id }}
        sourceEmail={emailDialogSource}
        organizationName={currentOrg?.name || ""}
        fromEmail="offers@mail.offertrace.com"
        fromDisplayName={`${currentOrg?.name || "OfferTrace"} via OfferTrace`}
        replyToEmail={null}
        configStatus={emailConfig}
        onClose={() => setEmailDialogOpen(false)}
        onSent={() => {
          setEmailDialogOpen(false);
          loadEmails();
          load();
        }}
      />
      <RenewDialog
        open={renewDialogOpen}
        source={{ ...q, id, has_file: hasFile }}
        customers={customers}
        submitting={renewSubmitting}
        error={renewError}
        onCancel={() => setRenewDialogOpen(false)}
        onConfirm={doRenew}
      />
    </Layout>
  );
}
